import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Select } from '@/shared/ui/primitives';
import apiClient from '@/lib/apiClient';

function extractList(res: any): any[] {
  const root = res?.data;
  const data = root?.data ?? root;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(root?.content)) return root.content;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

const cfg = (endpoint: string) => ({
  queryKey: ['entity-select', endpoint] as const,
  queryFn: async () => {
    const res = await apiClient.get(endpoint, { params: { page: 0, size: 500 } });
    return extractList(res);
  },
  staleTime: 60_000,
});

interface BaseSelectProps<T extends number | string> {
  value?: T | null;
  onChange?: (id: T | null, label?: string | null, option?: GeoSelectOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
  status?: 'error' | 'warning';
  allowClear?: boolean;
}

export interface GeoSelectOption {
  value: number;
  label: string;
  id: number;
  nameEn?: string | null;
  nameAr?: string | null;
  searchText: string;
  raw: any;
}

function buildLabel(row: any, lang: string) {
  const isAr = lang?.toLowerCase().startsWith('ar');
  const ar = row.nameAr || row.countryNameAr || row.geoCityNameAr || row.geoDistrictNameAr;
  const en = row.nameEn || row.countryNameEn || row.geoCityNameEn || row.geoDistrictNameEn;
  if (isAr) return ar || en || row.name || row.title || `#${row.id}`;
  return en || ar || row.name || row.title || `#${row.id}`;
}

function buildGeoOption(row: any, lang: string): GeoSelectOption {
  const nameEn = row.nameEn || row.countryNameEn || row.geoCityNameEn || row.geoDistrictNameEn || null;
  const nameAr = row.nameAr || row.countryNameAr || row.geoCityNameAr || row.geoDistrictNameAr || row.name || null;
  const label = buildLabel(row, lang);
  return {
    value: row.id,
    label,
    id: row.id,
    nameEn,
    nameAr,
    searchText: [label, nameEn, nameAr, row.name, row.title, row.id].filter(Boolean).join(' '),
    raw: row,
  };
}

function geoFilter(input: string, option: any) {
  return String(option?.searchText ?? option?.label ?? '').toLowerCase().includes(input.toLowerCase());
}

function useLocale() {
  const { i18n } = useTranslation();
  return i18n?.language || 'en';
}

/* -------------------- Country -------------------- */
export function CountrySelect({ value, onChange, placeholder, disabled, status, allowClear }: BaseSelectProps<number>) {
  const { data = [], isLoading } = useQuery(cfg('/admin/countries'));
  const lang = useLocale();
  return (
    <Select
      value={value ?? undefined}
      onChange={(v: any, opt: any) => onChange?.(v ?? null, opt?.label ?? null, opt ?? null)}
      options={(data as any[]).map((c) => ({ value: c.id, label: buildLabel(c, lang) }))}
      placeholder={placeholder ?? 'Select country'}
      showSearch
      allowClear={allowClear ?? true}
      filterOption={(input, option: any) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
      disabled={disabled}
      status={status}
      loading={isLoading}
    />
  );
}

/* -------------------- Region (optionally filtered by country) -------------------- */
export function RegionSelect({ countryId, value, onChange, placeholder, disabled, status, allowClear }: BaseSelectProps<number> & { countryId?: number | null }) {
  const { data = [], isLoading } = useQuery({
    ...cfg('/admin/regions'),
    queryKey: ['entity-select', '/admin/regions', countryId ?? null] as const,
    queryFn: async () => {
      const params: any = { page: 0, size: 500 };
      if (countryId) params.countryId = countryId;
      const res = await apiClient.get('/admin/regions', { params });
      return extractList(res);
    },
  });
  const lang = useLocale();
  return (
    <Select
      value={value ?? undefined}
      onChange={(v: any, opt: any) => onChange?.(v ?? null, opt?.label ?? null, opt ?? null)}
      options={(data as any[]).map((r) => ({ value: r.id, label: buildLabel(r, lang) }))}
      placeholder={placeholder ?? 'Select region'}
      showSearch
      allowClear={allowClear ?? true}
      filterOption={(input, option: any) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
      disabled={disabled}
      status={status}
      loading={isLoading}
    />
  );
}

/* -------------------- City (PostGIS-backed; ~4.5k SA cities) -------------------- */
export function useGeoCityOptions(regionId?: number | null) {
  const lang = useLocale();
  const query = useQuery({
    queryKey: ['entity-select', '/public/geo/cities', regionId ?? null] as const,
    queryFn: async () => {
      const res = await apiClient.get('/public/geo/cities', { params: { page: 0, size: 5000 } });
      return extractList(res);
    },
    staleTime: 60_000,
  });
  const options = (query.data ?? []).map((city: any) => buildGeoOption(city, lang));
  return { ...query, options };
}

export function CitySelect({ regionId, value, onChange, placeholder, disabled, status, allowClear }: BaseSelectProps<number> & { regionId?: number | null }) {
  const { options, isLoading } = useGeoCityOptions(regionId);
  return (
    <Select
      value={value ?? undefined}
      onChange={(v: any, opt: any) => onChange?.(v ?? null, opt?.label ?? null, opt ?? null)}
      options={options}
      placeholder={placeholder ?? 'Select city'}
      showSearch
      allowClear={allowClear ?? true}
      filterOption={geoFilter}
      disabled={disabled}
      status={status}
      loading={isLoading}
    />
  );
}

export function useGeoDistrictOptions(cityId?: number | string | null) {
  const lang = useLocale();
  const query = useQuery({
    queryKey: ['entity-select', '/public/geo/districts', cityId ?? null] as const,
    queryFn: async () => {
      if (!cityId) return [];
      const res = await apiClient.get('/public/geo/districts', { params: { page: 0, size: 2000, cityId } });
      return extractList(res);
    },
    enabled: !!cityId,
    staleTime: 60_000,
  });
  const options = (query.data ?? []).map((district: any) => buildGeoOption(district, lang));
  return { ...query, options };
}

/* -------------------- District (PostGIS-backed, filtered by city) -------------------- */
export function DistrictSelect({ cityId, value, onChange, placeholder, disabled, status, allowClear }: BaseSelectProps<number> & { cityId?: number | null }) {
  const { options, isLoading } = useGeoDistrictOptions(cityId);
  return (
    <Select
      value={value ?? undefined}
      onChange={(v: any, opt: any) => onChange?.(v ?? null, opt?.label ?? null, opt ?? null)}
      options={options}
      placeholder={placeholder ?? 'Select district'}
      showSearch
      allowClear={allowClear ?? true}
      filterOption={geoFilter}
      disabled={disabled}
      status={status}
      loading={isLoading}
    />
  );
}

/* -------------------- Simple lookups -------------------- */
function LookupSelect({ endpoint, value, onChange, placeholder, disabled, status, allowClear }: BaseSelectProps<number> & { endpoint: string }) {
  const { data = [], isLoading } = useQuery(cfg(endpoint));
  const lang = useLocale();
  return (
    <Select
      value={value ?? undefined}
      onChange={(v: any, opt: any) => onChange?.(v ?? null, opt?.label ?? null, opt ?? null)}
      options={(data as any[]).map((r) => ({ value: r.id, label: buildLabel(r, lang) }))}
      placeholder={placeholder}
      showSearch
      allowClear={allowClear ?? true}
      filterOption={(input, option: any) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
      disabled={disabled}
      status={status}
      loading={isLoading}
    />
  );
}

export const NationalitySelect = (p: BaseSelectProps<number>) => (
  <LookupSelect {...p} endpoint="/admin/nationalities" placeholder={p.placeholder ?? 'Select nationality'} />
);
export const HousingStatusSelect = (p: BaseSelectProps<number>) => (
  <LookupSelect {...p} endpoint="/admin/housing-statuses" placeholder={p.placeholder ?? 'Select housing status'} />
);
export const ReferralSelect = (p: BaseSelectProps<number>) => (
  <LookupSelect {...p} endpoint="/admin/referrals" placeholder={p.placeholder ?? 'Select referral'} />
);
export const PropertyAmenitySelect = (p: BaseSelectProps<number>) => (
  <LookupSelect {...p} endpoint="/admin/property-amenities" placeholder={p.placeholder ?? 'Select amenity'} />
);
